/**
 * BattleCode Demo · v1.1.0-demo
 * 入口文件
 *
 * 6 屏路由（v1.1.0 新增屏 0 首页）：
 *   #/                  → 屏 0 赛博指挥终端（首页）
 *   #/levels            → 屏 1 关卡列表
 *   #/editor/:id        → 屏 2 Vibe Coding 编辑器（含右侧 vibe-panel 侧栏直发）
 *   #/battle/:id        → 屏 4 战斗
 *   #/result/:id        → 屏 5/5.5 结果
 *   #/settings          → 屏 6 设置
 */

// 2026-07-05 T47.6:必须最早 import,这样后续 import 的模块调 console.* 都被拦截
import { startConsoleLogger } from "./utils/console-logger";
startConsoleLogger();

// 诊断:强制写一条测试日志,验证 console-logger → /api/log → browser.log 链路
console.info("[DIAG-START] main.ts loaded, console-logger enabled");

// V15: AudioEngine 自动初始化（首次用户交互时激活 AudioContext）
setupAutoInit();

// V15: 全局静音快捷键 — M 键切换静音/取消静音
document.addEventListener("keydown", (e) => {
  if (e.key === "m" && !e.ctrlKey && !e.metaKey && !e.altKey) {
    // 避免在文本输入框中误触发（Monaco Editor / input / textarea）
    const el = e.target as HTMLElement;
    const tag = el?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
    if (el?.closest(".monaco-editor")) return;
    e.preventDefault();
    audioEngine.toggleMute();
  }
});

import { ScreenRouter, pathFor, type RouteState, type ScreenId } from "./router/screen-router";
import { renderScreen0Title, cleanupTitleScreen } from "./screens/screen-0-title-screen";
import { renderScreen1Projects } from "./screens/screen-1-project-list";
import { renderScreen4Battle } from "./screens/screen-4-battle";
import { renderScreen5Result } from "./screens/screen-5-result";
import { renderScreen6Settings } from "./screens/screen-6-settings";
import { renderScreen7MapEditor } from "./screens/screen-7-map-editor";
import { renderScreen9ShowcaseValidator } from "./screens/screen-9-showcase-validator";
import { renderScreen10Sharpen } from "./screens/screen-10-sharpen";
import { renderScreen11SharpenTransition } from "./screens/screen-11-sharpen-transition";
import { renderScreen12Forge } from "./screens/screen-12-forge";
import { renderScreen13MyWorks, cleanupScreen13 } from "./screens/screen-13-my-works";
import { renderScreen14ArmoryHall } from "./screens/screen-14-armory-hall";
import { renderScreen15Transition } from "./screens/screen-15-transition";
import { renderAuthCallback, cleanupAuthCallback } from "./screens/screen-auth-callback";
import { renderScreen16SnapshotCollector } from "./screens/screen-16-snapshot-collector";
import { renderLevelLoadingPage } from "./screens/level-loading-page";

import { ensureDemoKeyPersisted } from "./services/llm-service";
import { t } from "./i18n";
import { validateTankStats } from "./engine/tank-stats";
import { audioEngine, setupAutoInit } from "./services/audio-engine";

// 2026-07-24 V11 性能优化:Monaco Editor(~10MB) 从首屏移除,改为动态 import
// - screen-2-editor 和 screen-8-arena 分别在首次导航时按需加载
// - monaco-setup.ts 作为 side-effect 模块,在 monaco-editor 之前初始化 workers
// - Vite 会自动将 Monaco + workers 分离为独立 chunk

// ===== 全局样式导入 =====
import "./styles/global.css";
import "./styles/header-bar.css";
import "./styles/screen-10-sharpen.css";
import "./styles/screen-11-sharpen-transition.css";
import "./styles/screen-12-forge.css";
import "./styles/screen-13-my-works.css";
import "./styles/screen-14-armory-hall.css";
import "./styles/screen-15-transition.css";
import "./styles/screen-16-snapshot-collector.css";
import "./styles/level-loading-page.css";
import "./styles/gm-tool.css";
import "./styles/onboarding.css";

// ===== GM 工具（隐藏入口：右下角 3 连击 / Ctrl+Shift+G） =====
import { setupGmTrigger } from "./services/gm-tool";
setupGmTrigger();

// P2-#2: 启动时校验 TANK_STATS 与 tank.json 一致性（非阻塞）
validateTankStats().then((ok) => {
  if (!ok) console.warn("[main] tank.json 与 TANK_STATS 不一致，以 TANK_STATS 为准");
});

// ===== 路由 =====
const router = new ScreenRouter();
let navigationEpoch = 0;
// V14: 追踪上一页面，供编辑器等页面的"返回上一级"使用
let lastScreen: string = "armory-hall";
let lastProjectId: string | null = null;
router.onChange((state) => {
  const app = document.getElementById("app");
  if (!app) return;
  const epoch = ++navigationEpoch;
  const prevScreen = lastScreen;
  const prevPid = lastProjectId;
  // 在当前 navigate 完成前先更新 lastScreen（避免同屏 self-back）
  lastScreen = state.screen;
  lastProjectId = state.projectId;
  void navigate(app, state, router, epoch, prevScreen, prevPid);
});

function isCurrentNavigation(epoch: number): boolean {
  return epoch === navigationEpoch;
}

/**
 * V14: 计算编辑器的返回路径。
 * 根据上一个页面的 screen + projectId 构造完整 hash 路径。
 * 无上页记录时回退到 armory-hall。
 */
function computeBackPath(prevScreen: string, prevPid: string | null): string {
  if (prevScreen === "home" || prevScreen === "armory-hall" || prevScreen === "transition") {
    return pathFor("armory-hall");
  }
  return pathFor(prevScreen as ScreenId, prevPid);
}

/**
 * 路由切换的统一边界：页面复用同一个 #app，因此必须同时清理资源和根节点样式。
 * 页面只允许给自己的子树设置动画样式，#app 本身保持无状态。
 */
function resetAppForNavigation(app: HTMLElement): void {
  cleanupTitleScreen(app);
  cleanupScreen13();
  cleanupAuthCallback(app);
  (app as any)._s4Cleanup?.();
  (app as any)._s7Cleanup?.();
  (app as any)._s14Cleanup?.();
  (app as any)._s15Cleanup?.();
  app.removeAttribute("style");
}

// 2026-07-24 V11 性能优化:navigate 改为 async,编辑器/斗蛐蛐用动态 import 按需加载 Monaco
async function navigate(app: HTMLElement, state: RouteState, router: ScreenRouter, epoch: number, prevScreen: string, prevPid: string | null) {
  // v1.1.0: 清理前一屏资源 + 重置 #app 根节点，防止动画状态跨页面泄漏
  resetAppForNavigation(app);

  // V15: 场景 BGM 过渡（在渲染新屏前触发）
  audioEngine.onScreenTransition(prevScreen, state.screen);

  const isCurrent = () => isCurrentNavigation(epoch);
  const go = (path: string) => router.go(path);
  try {
    if (!isCurrent()) return;
    switch (state.screen) {
      case "home":
        renderScreen0Title(app, (path: string) => go(path));
        break;
      case "levels":
      case "projects":
        renderScreen1Projects(app, go);
        break;
      case "editor":
        if (!state.projectId) {
          renderScreen1Projects(app, go);
        } else {
          // V14: 计算返回路径（上一页面，非 armory-hall）
          const backPath = computeBackPath(prevScreen, prevPid);
          // Monaco 首次动态加载期间保留明确的全屏过场，避免 #app 空白或误以为点击失效。
          const editorLoading = renderLevelLoadingPage(app, {
            mode: "page",
            title: "作战编辑器",
            subtitle: "正在装载 Monaco 编辑器与作战工作区",
          });
          editorLoading.setStage("editor", "加载 Monaco 编辑器与作战工作区");
          // 2026-07-24 动态 import:Monaco ~10MB 仅编辑器首次导航时加载
          const { renderScreen2Editor } = await import("./screens/screen-2-editor");
          if (!isCurrent()) return;
          renderScreen2Editor(app, state.projectId, go, backPath);
        }
        break;
      case "battle":
        if (!state.projectId) {
          renderScreen1Projects(app, go);
        } else {
          // 屏 4 是 async；把导航代际传入，阻止旧页面在新路由上完成挂载
          renderScreen4Battle(app, state.projectId, go, isCurrent).catch((e) => {
            if (isCurrent()) {
              app.innerHTML = `<div class="error">${t("error.screen4_failed")}: ${(e as Error).message}</div>`;
            }
          });
        }
        break;
      case "result":
        if (!state.projectId) {
          renderScreen1Projects(app, go);
        } else {
          renderScreen5Result(app, state.projectId, go);
        }
        break;
      case "settings":
        renderScreen6Settings(app, () => go(pathFor("armory-hall")));
        break;
      case "editor-map":
        // V2 屏 7: 地图编辑器
        // ★ 防御:state.projectId 可能在某条边角路径下变成空串(?? 不兜空串),用 || 兜底
        //   空串/空值都视为 "new",doSave 内部会 fork 到 user_<ts>
        renderScreen7MapEditor(app, state.projectId || "new", go, isCurrent).catch((e) => {
          if (isCurrent()) {
            app.innerHTML = `<div class="error">${t("error.screen7_failed")}: ${(e as Error).message}</div>`;
          }
        });
        break;
      case "arena":
        // 2026-07-24 动态 import:Monaco ~10MB 仅斗蛐蛐首次导航时加载
        {
          const { renderScreen8Arena } = await import("./screens/screen-8-arena");
          if (!isCurrent()) return;
          renderScreen8Arena(app, () => go(pathFor("armory-hall")));
        }
        break;
      case "showcase-validator":
        renderScreen9ShowcaseValidator(app, (path) => go(path));
        break;
      case "sharpen":
        renderScreen10Sharpen(app, go);
        break;
      case "sharpen-transition":
        // V8 屏 11 砺锋台中转页（强制中转,不可跳过,动画结束自动跳 #/sharpen）
        renderScreen11SharpenTransition(app, (path) => go(path));
        break;
      case "forge":
        renderScreen12Forge(go);
        break;
      case "my-works":
        // V9 屏 13: 我的作品集
        renderScreen13MyWorks(app, go);
        break;
      case "armory-hall":
        // V11 屏 14: 兵主殿（导航枢纽）
        renderScreen14ArmoryHall(app, (path: string) => go(path));
        break;
      case "transition":
        // V11 屏 15: 通用过渡动画（参数化）
        renderScreen15Transition(app, (path: string) => go(path));
        break;
      case "auth-callback":
        // V11: 微信登录回调
        renderAuthCallback(app, go);
        break;
      case "snapshot-collector":
        // V13 屏 16: Test Harness 快照采集
        renderScreen16SnapshotCollector(app, (path) => go(path));
        break;
    }
  } catch (e) {
    console.error("[navigate error]", e);
    app.innerHTML = `<div class="error">${t("error.screen_render_failed")}: ${(e as Error).message}</div>`;
  }
}

// ===== 启动 =====
router.start();

// 调试用：暴露 router 到 window
(window as any).__router = router;

// demo 阶段自动持久化预置 DeepSeek key（仅首次启动写入）
ensureDemoKeyPersisted();

// V14 账号唯一模式：匿名业务数据只清理，不迁移、不恢复。
import { purgeAnonymousStorage } from "./core/anonymous-cleanup";
void purgeAnonymousStorage();

// V12 数据中心 P1:初始化服务端同步客户端（注册钩子，自动拉取/推送）
import "./core/sync-client";
